import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, FileText, AlertTriangle } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const TermsPage = () => {
  useCanonical('https://ecuflashservice.com/terms');
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Terms of Service | ECU Flash Service</title>
        <meta name="description" content="ECU Flash Service terms of service. Read our terms and conditions for professional ECU tuning and file modification services." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-1 text-[#888] hover:text-[#00F0FF] transition text-sm font-semibold uppercase tracking-wider">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <FileText className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Legal</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Terms of</span>{' '}
            <span className="text-[#00F0FF]">Service</span>
          </h1>
          <p className="text-[#888] text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12">
          <div className="space-y-8">
            
            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                By accessing and using ECU Flash Service (&quot;the Service&quot;), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
              <p className="text-[#888] leading-relaxed">
                These terms apply to all users of the Service, including without limitation users who are browsers, customers, merchants, and/or contributors of content.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">2. Service Description</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                ECU Flash Service provides electronic engine control unit (ECU) file modification services. Our services include but are not limited to:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>ECU file analysis and diagnostics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Performance tuning and optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>DPF (Diesel Particulate Filter) software solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>EGR (Exhaust Gas Recirculation) software solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>AdBlue/SCR system software modifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>DTC (Diagnostic Trouble Code) removal</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Speed limiter adjustments</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Stage 1, Stage 2, and custom performance tuning</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">3. Intended Use & Legal Disclaimer</h2>
              <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-6 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#FF003C] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#FF003C] font-semibold mb-2">Important Notice</p>
                    <p className="text-[#FF003C]/80 text-sm">
                      All modifications to emission control systems (DPF, EGR, AdBlue/SCR, catalytic converters) are intended <strong>SOLELY for off-road, racing, and competition use</strong> in jurisdictions where such modifications are permitted. It is the customer responsibility to ensure compliance with all applicable local, state, and federal laws and regulations.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-[#888] leading-relaxed">
                By using our services, you confirm that you understand the modified files will be used in accordance with all applicable laws in your jurisdiction. ECU Flash Service shall not be held liable for any misuse of the modified files or any violations of environmental regulations.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">4. Customer Responsibilities</h2>
              <p className="text-[#888] leading-relaxed mb-4">As a customer, you agree to:</p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Provide accurate vehicle and ECU information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Ensure the original ECU file is correctly read and not corrupted</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Maintain a backup of your original ECU file before any modifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Use appropriate and compatible tools for reading and writing ECU files</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Understand that improper flashing procedures may damage your ECU or vehicle</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Comply with all applicable laws regarding vehicle modifications in your jurisdiction</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">5. Warranty & Guarantees</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                ECU Flash Service provides the following guarantees:
              </p>
              <ul className="space-y-2 text-[#888] mb-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">File Quality:</strong> Modified files are tested and verified before delivery</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">Rework Policy:</strong> Free rework if the modified file does not function as specified</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span><strong className="text-white">DTC Support:</strong> Free DTC correction for 30 days on the same file</span>
                </li>
              </ul>
              <p className="text-[#888] leading-relaxed">
                Our warranty does not cover: physical damage to ECU or vehicle components, issues caused by incorrect flashing procedures, use of incompatible or faulty reading/writing tools, or customer-provided incorrect vehicle information.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">6. Payment Terms</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                All payments are processed securely through PayPal. By making a purchase, you agree to:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Pay the full amount as displayed at checkout</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>All prices are in USD unless otherwise specified</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Payments are non-refundable once the modified file has been delivered</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Refunds may be issued at our discretion if the service cannot be completed</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">7. Intellectual Property</h2>
              <p className="text-[#888] leading-relaxed">
                All modified ECU files are provided for your personal use only. You may not redistribute, sell, or share modified files with third parties. ECU Flash Service retains all intellectual property rights to our modification methodologies, calibration data, and proprietary techniques.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                To the maximum extent permitted by law, ECU Flash Service shall not be liable for:
              </p>
              <ul className="space-y-2 text-[#888]">
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Any indirect, incidental, special, consequential, or punitive damages</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Damage to your vehicle, engine, or ECU resulting from modifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Loss of vehicle warranty due to ECU modifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Legal penalties or fines resulting from use of modified files</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00F0FF] mt-1">▸</span>
                  <span>Any damages exceeding the amount paid for the specific service</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">9. Modifications to Terms</h2>
              <p className="text-[#888] leading-relaxed">
                ECU Flash Service reserves the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of the Service after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">10. Contact Information</h2>
              <p className="text-[#888] leading-relaxed">
                If you have any questions about these Terms of Service, please contact us through our <Link to="/contact" className="text-[#00F0FF] hover:underline">Contact Page</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-4">11. Business Information</h2>
              <p className="text-[#888] leading-relaxed mb-4">
                This website and services are operated by:
              </p>
              <div className="bg-[#1A1A1A] border border-white/10 p-6">
                <p className="text-[#888] mb-2"><strong className="text-white">Trading Name:</strong> ECU Flash Service</p>
                <p className="text-[#888] mb-2"><strong className="text-white">Business Type:</strong> Sole Proprietorship</p>
                <p className="text-[#888]"><strong className="text-white">Contact:</strong> <a href="mailto:support@ecuflashservice.com" className="text-[#00F0FF] hover:underline">support@ecuflashservice.com</a></p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
